import { useMemo, useCallback, useState, useRef } from 'react';
import _ from 'underscore';
import { SuggestionPoller } from '@bingads-webui-campaign/media-repository';
import { MIN_SIZE } from '../../../src/model/constants';

export const useWebsiteImages = ({
  cid, aid, oDataConfig, setSelectedImages,
}) => {
  const [images, setImages] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoadingFistPage, setIsLoadingFirstPage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const pageNumber = useRef(0);
  const selectedImages = useRef([]);

  const suggestionPoller = useMemo(() => new SuggestionPoller({
    cid,
    aid,
    oDataConfig,
    logger: {
      trace: _.noop,
      startSignal: _.noop,
      stopSignal: _.noop,
    },
    jobCompleteCallback: _.noop,
  }), [cid, aid, oDataConfig]);

  const setPageNumber = useCallback((newPageNumber) => {
    pageNumber.current = newPageNumber;
  }, []);

  const getLandingPageSuggestions = useCallback(({
    landingPageUrl,
    isSubdomain,
    pageNum,
  }) => {
    const minimumResolution = {
      width: MIN_SIZE[0],
      height: MIN_SIZE[1],
    };
    if (pageNum === 1) {
      // eslint-disable-next-line no-restricted-globals
      return new Promise((resolve) => {
        suggestionPoller.setJobCompleteCallback(resolve);
        suggestionPoller.getLandingPageSuggestions({
          landingPageUrl,
          forceInternalScrapingService: isSubdomain,
          minimumResolution,
          pageNumber: pageNum,
        });
      });
    }
    return suggestionPoller.getMoreLandingPageSuggestions({
      landingPageUrl,
      pageNumber: pageNum,
      forceInternalScrapingService: isSubdomain,
      minimumResolution,
    });
  }, [suggestionPoller]);
  /* istanbul ignore next */
  const loadImages =
    useCallback(({ url = '', sDomain = '' }) => {
      if (url) {
        if (pageNumber.current === 1) {
          selectedImages.current = [];
          setSelectedImages(selectedImages.current);
          setIsLoadingFirstPage(true);
        }
        setIsLoading(true);
        getLandingPageSuggestions({
          landingPageUrl: url,
          isSubdomain: !!sDomain,
          pageNum: pageNumber.current,
        }).then((data) => {
          const { TotalCount } = data.ImagePaging;
          setTotalCount(TotalCount);
          setImages(prevImages => [
            ...(pageNumber.current > 1 ? prevImages : []),
            ..._.map(data.Images, (item) => {
              const image = {
                url: item.ThumbnailUrl || item.ImageUrl,
                id: item.ImageUrl,
              };

              return image;
            }),
          ]);

          if (pageNumber.current === 1) {
            setIsLoadingFirstPage(false);
          }

          setIsLoading(false);
        }).catch(() => {
          if (pageNumber.current === 1) {
            setIsLoadingFirstPage(false);
          }

          setIsLoading(false);
        });
      }
    }, [getLandingPageSuggestions, setSelectedImages]);
  /* istanbul ignore else */
  const loadNextPage = useCallback((props) => {
    pageNumber.current += 1;
    loadImages(props);
  }, [loadImages]);

  const onSelectImage = useCallback((imageId) => {
    const originSelectedLength = selectedImages.current.length;
    const newSelectedList = selectedImages.current.filter(({ id }) => id !== imageId);
    /* istanbul ignore else */
    if (newSelectedList.length === originSelectedLength) {
      const selectedItem = images.find(({ id }) => id === imageId);
      selectedImages.current.push(selectedItem);
    } else {
      selectedImages.current = newSelectedList;
    }
    setSelectedImages([...selectedImages.current]);
  }, [images, setSelectedImages]);

  return {
    images,
    totalCount,
    isLoadingFistPage,
    isLoading,
    setImages,
    loadNextPage,
    loadImages,
    setPageNumber,
    onSelectImage,
  };
};
